import type { AnimationControls } from 'framer-motion';
import type { ReactElement, SetStateAction } from 'react';
import React, {
  createContext,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';

import { useIsMobileSize } from 'src/modules/renderings/hooks';
import {
  type User,
  useProfileQuery,
  usePublicOrgInfoQuery,
} from 'src/shared/graphql/generated';
import { useAuthExperience } from 'src/shared/hooks/useAuthExperience';
import type { CreateLeadResponse } from 'src/shared/requests/createLead';

export enum ProPageSheetSteps {
  LEAD = 'lead',
  CREATE_ACCOUNT = 'create-account',
  FOUND_ACCOUNT = 'found-account',
}

const initialLeadData = {
  email: undefined,
  firstName: undefined,
  lastName: undefined,
  phoneNumber: undefined,
  id: undefined,
};

export const initialContext = {
  loading: false,
  loggedIn: false,
  orgDisplayName: undefined,
  orgId: undefined,
  brandLogoUrl: undefined,
  leadData: initialLeadData,
  imageFileToUpload: undefined,
  leadSubmitted: false,
  leadFormRef: undefined,
  isLeadFormVisible: false,
  publicIdentifier: '',
  headerOffsetControls: undefined,
  setHeaderOffsetControls: () => null,
  setImageFileToUpload: () => null,
  setIsLeadFormVisible: () => null,
  dispatchLeadResponse: () => null,
  proPageSheetStep: ProPageSheetSteps.LEAD,
  setProPageSheetStep: () => null,
  genericTrackingData: { device_type: undefined },
};

export interface ProPageContextProps {
  loading: boolean;
  loggedIn: boolean;
  orgDisplayName?: string;
  orgId?: number;
  leadData: LeadData;
  brandLogoUrl?: string | null;
  imageFileToUpload?: File | undefined;
  leadSubmitted: boolean;
  leadFormRef: React.MutableRefObject<HTMLDivElement | null> | undefined;
  isLeadFormVisible: boolean;
  publicIdentifier: string;
  headerOffsetControls?: AnimationControls;
  setHeaderOffsetControls: React.Dispatch<
    SetStateAction<undefined | AnimationControls>
  >;
  setImageFileToUpload: (imageFile: File | undefined) => void;
  setIsLeadFormVisible: (isVisible: boolean) => void;
  dispatchLeadResponse: (payload: CreateLeadResponse) => void;
  proPageSheetStep: ProPageSheetSteps;
  setProPageSheetStep: (step: ProPageSheetSteps) => void;
  genericTrackingData: {
    device_type?: string;
    feature?: string;
    pro_page_org_id?: number;
    page_or_screen_name?: string;
  };
}

const proPageContext = createContext<ProPageContextProps>(initialContext);

interface LeadData {
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  id?: number;
}

const ProPageProvider = ({ children }: { children: ReactElement }) => {
  useAuthExperience();
  const { publicIdentifier } = useParams<{ publicIdentifier: string }>();
  const isMobile = useIsMobileSize();

  const leadFormReducer = (state: LeadData, payload: CreateLeadResponse) => {
    if (payload) {
      const name_array = payload.full_name.split(' ');
      return {
        ...state,
        email: payload.email,
        firstName: name_array.length > 0 ? name_array[0] : '',
        lastName:
          name_array.length > 1 ? name_array[name_array.length - 1] : '',
        phoneNumber: payload.phone_number,
        id: payload.id,
      };
    }

    return state;
  };

  const [leadData, dispatchLeadResponse] = useReducer(leadFormReducer, {
    ...initialLeadData,
  });
  const [imageFileToUpload, setImageFileToUpload] = useState<
    File | undefined
  >();
  const [headerOffsetControls, setHeaderOffsetControls] = useState<
    AnimationControls | undefined
  >(undefined);

  const leadFormRef = useRef<HTMLDivElement>(null);
  const [isLeadFormVisible, setIsLeadFormVisible] = useState(false);

  const { data, loading: publicOrgInfoLoading } = usePublicOrgInfoQuery({
    variables: {
      publicIdentifier,
    },
  });
  const { data: profileData, loading } = useProfileQuery({
    errorPolicy: 'none',
  });
  const [proPageSheetStep, setProPageSheetStep] = useState<ProPageSheetSteps>(
    ProPageSheetSteps.LEAD,
  );

  const { brandLogoUrl, displayName, id: orgId } = data?.publicOrgInfo || {};
  const currentUser = profileData?.profile?.user as User;

  const value = useMemo(
    () => ({
      loading: loading || publicOrgInfoLoading,
      loggedIn: !!currentUser,
      leadSubmitted: !!leadData.id,
      brandLogoUrl,
      orgDisplayName: displayName,
      orgId,
      leadData,
      imageFileToUpload,
      leadFormRef,
      isLeadFormVisible,
      publicIdentifier,
      headerOffsetControls,
      setHeaderOffsetControls,
      dispatchLeadResponse,
      setImageFileToUpload,
      setIsLeadFormVisible,
      proPageSheetStep,
      setProPageSheetStep,
      genericTrackingData: {
        device_type: isMobile ? 'mobile' : 'desktop',
        feature: 'lead-landing-page',
        pro_page_org_id: orgId,
        page_or_screen_name: 'lead-landing-page-v1',
      },
    }),
    [
      loading,
      publicOrgInfoLoading,
      currentUser,
      leadData,
      brandLogoUrl,
      displayName,
      orgId,
      imageFileToUpload,
      leadFormRef,
      isLeadFormVisible,
      headerOffsetControls,
      setHeaderOffsetControls,
      setIsLeadFormVisible,
      publicIdentifier,
      proPageSheetStep,
      setProPageSheetStep,
      isMobile,
    ],
  );

  return (
    <proPageContext.Provider value={value}>{children}</proPageContext.Provider>
  );
};

export { ProPageProvider, proPageContext };
